import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  User,
  LayoutDashboard,
  Package,
  FileCheck,
  ShieldCheck,
  CheckCircle,
  XCircle,
  ShoppingCart,
} from "lucide-react";

const Navbar = () => {
  const { seller, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState({
    panVerified: false,
    gstVerified: false,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  // Check verification status from localStorage
  useEffect(() => {
    const checkVerification = () => {
      const sellerPan = localStorage.getItem("sellerPanNumber");
      const sellerGst = localStorage.getItem("sellerGstin");

      setVerificationStatus({
        panVerified: !!(
          sellerPan &&
          sellerPan !== "null" &&
          sellerPan !== "undefined" &&
          sellerPan.trim() !== ""
        ),
        gstVerified: !!(
          sellerGst &&
          sellerGst !== "null" &&
          sellerGst !== "undefined" &&
          sellerGst.trim() !== ""
        ),
      });
    };

    checkVerification();
    const interval = setInterval(checkVerification, 1000);
    window.addEventListener("storage", checkVerification);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkVerification);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navLinks = seller
    ? [
        {
          label: "Dashboard",
          to: "/dashboard",
          icon: LayoutDashboard,
        },
        {
          label: "Products",
          to: "/products",
          icon: Package,
        },
        {
          label: "Orders",
          to: "/orders",
          icon: ShoppingCart,
        },
        {
          label: "KYC",
          to: "/kyc",
          icon: FileCheck,
          verified: verificationStatus.panVerified,
          verifiedIcon: (
            <CheckCircle size={14} className="text-green-500 ml-1" />
          ),
          unverifiedIcon: <XCircle size={14} className="text-amber-500 ml-1" />,
        },
        {
          label: "GST",
          to: "/gst",
          icon: ShieldCheck,
          verified: verificationStatus.gstVerified,
          verifiedIcon: (
            <CheckCircle size={14} className="text-green-500 ml-1" />
          ),
          unverifiedIcon: <XCircle size={14} className="text-amber-500 ml-1" />,
        },
      ]
    : [
        { label: "Why Sell?", to: "/#why-sell", icon: null },
        { label: "Register", to: "/register", icon: null },
        { label: "Login", to: "/login", icon: null },
      ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[900] transition-all duration-400 ease-in-out ${
        scrolled
          ? "bg-white/95 backdrop-blur-2xl border-b border-white/10 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 sm:gap-3 shrink-0 group"
        >
          <img
            src="/harappa-logo1.png"
            alt="Harappa Logo"
            className="w-28 sm:w-38 h-16 sm:h-22 object-contain"
          />
          <span className="bg-gradient-to-br from-accent to-orange-500 text-white text-[8px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full tracking-widest uppercase">
            Seller
          </span>
        </Link>

        {/* Desktop Navigation - Hidden on mobile */}
        <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === link.to
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-slate-600 hover:bg-primary/5 hover:text-primary"
              }`}
            >
              {link.icon && <link.icon size={15} />}
              <span className="flex items-center">
                {link.label}
                {"verified" in link &&
                  link.verified !== undefined &&
                  (link.verified
                    ? (link as any).verifiedIcon
                    : (link as any).unverifiedIcon)}
              </span>
            </Link>
          ))}
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-2 sm:gap-3">
          {seller ? (
            <>
              {/* Notification Bell - Hidden on very small screens */}
              <button className="hidden sm:flex w-8 h-8 sm:w-10 sm:h-10 bg-slate-100/50 border border-slate-200 rounded-full items-center justify-center text-slate-600 hover:border-primary hover:text-primary transition-all relative">
                <Bell size={18} />
                <span className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full border-1.5 border-white" />
              </button>

              {/* User Dropdown */}
              <div className="relative">
                <div
                  className="flex items-center gap-1 sm:gap-2.5 p-1 pl-1 sm:pl-1 pr-2 sm:pr-3 bg-slate-100/50 border border-slate-200 rounded-full cursor-pointer hover:border-primary transition-all select-none"
                  onClick={() => setDropOpen(!dropOpen)}
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-xs font-bold text-white shadow-md shadow-primary/10">
                    {seller.email?.[0]?.toUpperCase()}
                  </div>
                  <div className="hidden sm:flex flex-col text-left leading-tight max-w-[120px]">
                    <span className="text-xs font-semibold text-slate-900 truncate">
                      {seller.email}
                    </span>
                    <span className="text-[10px] text-slate-400">Seller</span>
                  </div>
                  <ChevronDown
                    size={14}
                    className={`text-slate-400 transition-transform flex-shrink-0 ${
                      dropOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {/* Dropdown Menu */}
                {dropOpen && (
                  <div className="absolute top-[calc(100%+8px)] right-0 min-w-[220px] sm:min-w-[240px] bg-white border border-slate-100 rounded-xl p-2 shadow-2xl shadow-slate-200/50 z-[100] animate-fade-in">
                    {/* <Link
                      to="/dashboard"
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      onClick={() => setDropOpen(false)}
                    >
                      <LayoutDashboard size={16} className="opacity-70" />
                      Dashboard
                    </Link> */}
                    <Link
                      to="/profile"
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      onClick={() => setDropOpen(false)}
                    >
                      <User size={16} className="opacity-70" />
                      Profile
                    </Link>
                    <Link
                      to="/products"
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      onClick={() => setDropOpen(false)}
                    >
                      <Package size={16} className="opacity-70" />
                      Notification
                    </Link>

                    {/* Verification Status */}
                    {/* <div className="px-3 py-2 mt-1 border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">PAN:</span>
                        <span
                          className={`flex items-center gap-1 font-medium ${
                            verificationStatus.panVerified
                              ? "text-green-600"
                              : "text-amber-600"
                          }`}
                        >
                          {verificationStatus.panVerified ? (
                            <>
                              <CheckCircle size={12} />
                              Verified
                            </>
                          ) : (
                            <>
                              <XCircle size={12} />
                              Pending
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <span className="text-slate-500">GST:</span>
                        <span
                          className={`flex items-center gap-1 font-medium ${
                            verificationStatus.gstVerified
                              ? "text-green-600"
                              : "text-amber-600"
                          }`}
                        >
                          {verificationStatus.gstVerified ? (
                            <>
                              <CheckCircle size={12} />
                              Verified
                            </>
                          ) : (
                            <>
                              <XCircle size={12} />
                              Pending
                            </>
                          )}
                        </span>
                      </div>
                    </div> */}

                    <div className="h-px bg-slate-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 p-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
                    >
                      <LogOut size={18} className="opacity-70" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Non-logged in user actions */
            <>
              <div className="hidden sm:flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-primary transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-br from-accent to-orange-500 rounded-full hover:shadow-lg hover:shadow-accent/20 transition-all"
                >
                  Start Selling
                </Link>
              </div>
              {/* Mobile auth buttons */}
              <div className="flex sm:hidden items-center gap-2">
                <Link
                  to="/login"
                  className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-full hover:border-primary transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-br from-accent to-orange-500 rounded-full"
                >
                  Sell
                </Link>
              </div>
            </>
          )}

          {/* Mobile Menu Button - Now uncommented and visible */}
          <button
            className="lg:hidden w-8 h-8 sm:w-10 sm:h-10 bg-slate-100/50 border border-slate-200 text-slate-600 rounded-lg flex items-center justify-center hover:border-primary hover:text-primary transition-all ml-1"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Slide down animation */}
      <div
        className={`lg:hidden fixed left-0 right-0 bg-white/95 backdrop-blur-2xl border-t border-slate-100 shadow-xl transition-all duration-300 ease-in-out ${
          menuOpen
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible -translate-y-2 pointer-events-none"
        }`}
        style={{
          top: "64px",
          maxHeight: "calc(100vh - 64px)",
          overflowY: "auto",
        }}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center justify-between gap-3 p-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              <div className="flex items-center gap-3">
                {link.icon && <link.icon size={18} className="opacity-70" />}
                <span>{link.label}</span>
              </div>
              {"verified" in link && link.verified !== undefined && (
                <span
                  className={`flex items-center gap-1 text-xs ${
                    link.verified ? "text-green-600" : "text-amber-600"
                  }`}
                >
                  {link.verified ? (
                    <>
                      <CheckCircle size={14} />
                      Verified
                    </>
                  ) : (
                    <>
                      <XCircle size={14} />
                      Pending
                    </>
                  )}
                </span>
              )}
            </Link>
          ))}

          {/* Seller-specific mobile menu items */}
          {seller && (
            <>
              {/* Notification for mobile */}
              <button className="flex items-center gap-3 p-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors lg:hidden">
                <Bell size={18} className="opacity-70" />
                <span>Notifications</span>
                <span className="ml-auto w-2 h-2 bg-red-500 rounded-full" />
              </button>

              {/* Profile link for mobile */}
              <Link
                to="/profile"
                className="flex items-center gap-3 p-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors lg:hidden"
                onClick={() => setMenuOpen(false)}
              >
                <User size={18} className="opacity-70" />
                <span>Profile</span>
              </Link>

              {/* Verification Status in Mobile */}
              <div className="mt-2 p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 font-medium">
                    PAN Status:
                  </span>
                  <span
                    className={`flex items-center gap-1 ${
                      verificationStatus.panVerified
                        ? "text-green-600"
                        : "text-amber-600"
                    }`}
                  >
                    {verificationStatus.panVerified ? (
                      <>
                        <CheckCircle size={12} />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle size={12} />
                        Pending
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs mt-2">
                  <span className="text-slate-600 font-medium">
                    GST Status:
                  </span>
                  <span
                    className={`flex items-center gap-1 ${
                      verificationStatus.gstVerified
                        ? "text-green-600"
                        : "text-amber-600"
                    }`}
                  >
                    {verificationStatus.gstVerified ? (
                      <>
                        <CheckCircle size={12} />
                        Verified
                      </>
                    ) : (
                      <>
                        <XCircle size={12} />
                        Pending
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* Logout button for mobile */}
              <button
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
                className="flex items-center gap-3 p-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left mt-2"
              >
                <LogOut size={18} className="opacity-70" />
                Logout
              </button>
            </>
          )}

          {/* Non-logged in user mobile menu items */}
          {!seller && (
            <div className="flex flex-col gap-2 mt-4">
              <Link
                to="/login"
                className="w-full px-4 py-3 text-center text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:border-primary transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                Login
              </Link>
              <Link
                to="/register"
                className="w-full px-4 py-3 text-center text-sm font-medium text-white bg-gradient-to-br from-accent to-orange-500 rounded-xl hover:shadow-lg transition-all"
                onClick={() => setMenuOpen(false)}
              >
                Start Selling
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
